import type { Me } from '../api/types';
import { resolveEntry } from '../api/types';

/**
 * Карта экранов: заголовок в шапке, корень раздела и родитель подэкрана.
 * Перенесена из `CONF`, `TABS` и `SUBS` прототипа — там же лежат и тексты.
 *
 * `root: true` — экран, с которого раздел начинается: на нём кнопка «назад»
 * не показывается ни в шапке, ни в чате Telegram.
 */

export type ScreenId =
  | 'home' | 'pick' | 'service' | 'calendar' | 'form' | 'confirm' | 'done'
  | 'nb-welcome' | 'nb-child' | 'nb-dir' | 'nb-result' | 'nb-sent'
  | 's-today' | 's-cal' | 's-kids' | 's-hours' | 's-off'
  | 'a-center' | 'a-staff' | 'a-services' | 'a-clients' | 'a-org'
  | 'a-staff-add' | 'a-staff-services' | 'a-staff-schedule'
  | 'a-service' | 'a-service-add' | 'a-dirs' | 'a-client-add' | 'a-series'
  | 'a-org-profile' | 'a-org-addresses' | 'a-org-address' | 'a-org-schedule'
  | 'a-org-notifications' | 'a-org-subscription'
  | 'a-org-hours' | 'a-org-categories' | 'a-org-admins' | 'a-org-audit'
  | 'p-tenants' | 'p-settings' | 'p-new' | 'p-tenant';

export type Section = 'client' | 'nb' | 'spec' | 'admin' | 'platform';

/** Роль в правом верхнем углу шапки — как в прототипе. */
export const ROLE_LABEL: Record<Section, string> = {
  client: '',
  nb: 'первый вход',
  spec: 'специалист',
  admin: 'админ',
  platform: 'владелец',
};

/**
 * Подпись роли для текущего человека и раздела.
 *
 * Владелец сервиса, вошедший в чужую организацию, работает в её админке —
 * `requireRole` пропускает его везде, где нужен админ, — но администратором
 * этой организации не является. Подпись «админ» над полосой «Режим владельца
 * сервиса» противоречила сама себе; называем того, чьи права действуют.
 */
export function roleLabelFor(me: Me, section: Section): string {
  if (me.ownerVisit && (section === 'admin' || section === 'spec')) return ROLE_LABEL.platform;
  return ROLE_LABEL[section];
}

export type Tab = { screen: ScreenId; path: string; label: string; icon: string };

/** Иконки — те же пути, что в прототипе: 'circle' раскрывается в <circle r=9>. */
export const TABS: Record<'spec' | 'admin' | 'platform', Tab[]> = {
  spec: [
    { screen: 's-today', path: '/staff', label: 'Мой день', icon: 'M4 5h16v15H4z|M8 3v4M16 3v4M4 10h16' },
    { screen: 's-cal', path: '/staff/calendar', label: 'Календарь', icon: 'M4 5h16v15H4z|M8 3v4M16 3v4M4 10h16M9 14h2M15 14h2' },
    { screen: 's-kids', path: '/staff/kids', label: 'Дети', icon: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4 21c0-4 4-6 8-6s8 2 8 6' },
    { screen: 's-hours', path: '/staff/hours', label: 'Расписание', icon: 'M12 7v5l3 2|circle' },
    { screen: 's-off', path: '/staff/off', label: 'Особые дни', icon: 'M4 5h16v15H4z|M9 14l6 6M15 14l-6 6' },
  ],
  admin: [
    { screen: 'a-center', path: '/admin', label: 'Сегодня', icon: 'M4 5h16v15H4z|M8 3v4M16 3v4M4 10h16' },
    { screen: 'a-staff', path: '/admin/staff', label: 'Специалисты', icon: 'M8 11a3 3 0 100-6 3 3 0 000 6zM2 20c0-3 3-5 6-5s6 2 6 5|M17 11a3 3 0 100-6' },
    { screen: 'a-services', path: '/admin/services', label: 'Услуги', icon: 'M4 7h16v13H4z|M8 7V4h8v3M9 13h6' },
    { screen: 'a-clients', path: '/admin/clients', label: 'Клиенты', icon: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4 21c0-4 4-6 8-6s8 2 8 6' },
    { screen: 'a-org', path: '/admin/org', label: 'Организация', icon: 'M3 20h18M6 20V9l6-4 6 4v11|M10 20v-5h4v5' },
  ],
  platform: [
    { screen: 'p-tenants', path: '/owner', label: 'Организации', icon: 'M4 20h16M7 20V8h10v12|M10 12h4M10 16h4' },
    { screen: 'p-settings', path: '/owner/settings', label: 'Платформа', icon: 'M12 15a3 3 0 100-6 3 3 0 000 6z|M4 12h2M18 12h2M12 4v2M12 18v2' },
  ],
};

/**
 * Куда открывается вход.
 *
 * Совмещающий роли начинает день не с организации, а со своих приёмов, поэтому
 * входит он в рабочее место: админка у него в шаге переключателя роли, а вот
 * обратного пути из админки не было бы вовсе. Администратор без карточки
 * специалиста ведёт только организацию — ему по-прежнему в админку.
 */
export function rootPathFor(me: Me): string {
  switch (resolveEntry(me)) {
    case 'owner': return '/owner';
    case 'staff': return me.specialistId ? '/staff' : '/admin';
    case 'onboarding': return '/onboarding';
    default: return '/';
  }
}

/**
 * Корень раздела, в котором человек сейчас находится: сюда ведёт «назад» с
 * подэкрана, когда истории нет. Считается по адресу, а не по роли — иначе
 * «назад» из карточки услуги уносило бы совмещающего роли в чужой раздел.
 */
export function rootPathOfSection(section: Section): string {
  switch (section) {
    case 'platform': return '/owner';
    case 'admin': return '/admin';
    case 'spec': return '/staff';
    case 'nb': return '/onboarding';
    default: return '/';
  }
}

/**
 * Куда возвращает «назад», когда истории нет: страницу открыли по прямой ссылке.
 *
 * Карточка организации — единственное место с двумя уровнями подэкранов, и
 * возврат из «Уведомлений» в «Сегодня» выглядел бы промахом мимо цели. Остальные
 * разделы плоские, им хватает корня.
 */
export function backPathOf(pathname: string, section: Section): string {
  if (pathname.startsWith('/admin/org/')) return '/admin/org';
  return rootPathOfSection(section);
}

export function sectionOfPath(pathname: string, me: Me): Section {
  if (pathname.startsWith('/owner')) return 'platform';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/staff')) return 'spec';
  if (pathname.startsWith('/onboarding')) return 'nb';
  return me.isSuper ? 'platform' : 'client';
}
